import { logGreyErrorFromAccounts } from "owa-account-analytics";
import type { CoprincipalAccountSource } from "../store/schema/AccountSourceList";
import { BootState } from "../store/schema/BootState";

/**
 * Subscriber invoked from `setCoprincipalAccountBootState` when a coprincipal account
 * transitions boot states. The setter can await the subscriber, so the subscriber can
 * hook blocking async work into boot state transitions (e.g. hooking account analytics
 * init into account startup).
 */
export type BlockingBootStateCallback = (account: CoprincipalAccountSource) => Promise<void>;

const subscribersByState = new Map<BootState, Set<BlockingBootStateCallback>>();

/**
 * Register a callback to be invoked (and awaited) by `setCoprincipalAccountBootState`
 * whenever a coprincipal account transitions to `state`. Returns a dispose function
 * that removes the callback.
 */
export function addBlockingBootStateSubscriber(state: BootState, callback: BlockingBootStateCallback): () => void {
    let set = subscribersByState.get(state);
    if (!set) {
        set = new Set();
        subscribersByState.set(state, set);
    }
    set.add(callback);
    return () => {
        subscribersByState.get(state)?.delete(callback);
    };
}

/**
 * Invoke all subscribers registered for `state` against `account` and await them
 * concurrently. Fast no-op when nothing is registered.
 */
export async function invokeBlockingBootStateSubscribers(account: CoprincipalAccountSource, state: BootState): Promise<void> {
    const set = subscribersByState.get(state);
    if (!set || set.size === 0) {
        return;
    }
    await Promise.all([...set].map(async (callback) => {
        try {
            await callback(account);
        }
        catch (error) {
            logGreyErrorFromAccounts("BlockingBootStateSubscriberError", account.mailboxInfo, error as Error | undefined, { bootState: BootState[state] });
        }
    }));
}
